/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileDown, Image as ImageIcon, Pencil, CheckCircle2, Quote } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Promotion Letter — branded design per company:
 *  - NDB Holding Agrícola → green theme + "plantio mecanizado"
 *  - Usina ALCON → navy + green theme + 4 pillars (Energia/Açúcar/Etanol/Levedura)
 *  - Sayonara → ochre/copper theme + "colheita mecanizada"
 *  - Other → fallback green theme
 */
const HERO_IMG = "https://images.unsplash.com/photo-1560493676-04071c5f467b?auto=format&fit=crop&w=1600&q=80";
// ^ sugarcane field

function getCompanyTheme(companyName) {
  const n = (companyName || "").toLowerCase();
  if (n.includes("ndb")) {
    return {
      key: "ndb",
      primary: "#1F8136", // NDB green
      secondary: "#34A853",
      tint: "#E5F4E8",
      accentBand: "linear-gradient(180deg, #2A9445 0%, #1F8136 100%)",
      heroGradient: "linear-gradient(135deg, rgba(31,129,54,0.95) 0%, rgba(52,168,83,0.85) 50%, rgba(31,129,54,0.4) 100%)",
      footerBg: "#1F8136",
      pillars: ["Plantio Mecanizado", "Preparo de Solo", "Tecnologia Agrícola"],
      story: "Aqui na NDB Holding, somos especialistas em plantio mecanizado de cana-de-açúcar, utilizando tecnologia de ponta para garantir a melhor produtividade. Por isso, uma pessoa como você, que inspira e transforma, reforça nossa posição como líder em soluções agrícolas inovadoras.",
      contact: "Rod. BR 101 — Sayonara — Norte — Km 35,5 — Conceição da Barra/ES\nalcon@usinaalcon.com.br · +55 27 2233-3322",
      addressShort: "NDB Holding — Conceição da Barra/ES",
    };
  }
  if (n.includes("sayonara")) {
    return {
      key: "sayonara",
      primary: "#8C4A28",
      secondary: "#B26A3D",
      tint: "#F4E8DD",
      accentBand: "linear-gradient(180deg, #A45F32 0%, #8C4A28 100%)",
      heroGradient: "linear-gradient(135deg, rgba(140,74,40,0.95) 0%, rgba(178,106,61,0.85) 50%, rgba(140,74,40,0.4) 100%)",
      footerBg: "#8C4A28",
      pillars: ["Colheita Mecanizada", "Transporte", "Logística"],
      story: "Aqui na Sayonara, somos especialistas em colheita mecanizada de cana-de-açúcar, contribuindo para a eficiência e sustentabilidade do agronegócio. Por isso, uma pessoa como você, que inspira e transforma, reforça nossa posição como referência em operações agrícolas de alta performance.",
      contact: "Rod. BR 101 — Sayonara — Norte — Km 35,5 — Conceição da Barra/ES\nalcon@usinaalcon.com.br · +55 27 2233-3322",
      addressShort: "Sayonara — Conceição da Barra/ES",
    };
  }
  if (n.includes("alcon") || n.includes("usina")) {
    return {
      key: "alcon",
      primary: "#1A2C58", // Navy
      secondary: "#234935", // Green
      tint: "#E8EEF5",
      accentBand: "linear-gradient(180deg, #234935 0%, #1A2C58 100%)",
      heroGradient: "linear-gradient(135deg, rgba(26,44,88,0.95) 0%, rgba(35,73,53,0.85) 50%, rgba(26,44,88,0.4) 100%)",
      footerBg: "#1A2C58",
      pillars: ["Energia", "Açúcar", "Etanol", "Levedura"],
      story: "Aqui na Usina Alcon, temos o propósito de produzir com qualidade e de forma sustentável. Por isso, uma pessoa como você, que inspira e transforma, reforça nossa posição como uma das maiores usinas do Espírito Santo, gerando energia renovável e excelência em produtos agroindustriais.",
      contact: "Rod. BR 101 — Sayonara — Norte — Km 35,5 — Conceição da Barra/ES\nalcon@usinaalcon.com.br · +55 27 2233-3322",
      addressShort: "Usina Alcon — Conceição da Barra/ES",
    };
  }
  return {
    key: "default",
    primary: "#234935", secondary: "#C5A059", tint: "#FAF4E5",
    accentBand: "linear-gradient(180deg, #2c5c43 0%, #234935 100%)",
    heroGradient: "linear-gradient(135deg, rgba(35,73,53,0.95) 0%, rgba(197,160,89,0.6) 100%)",
    footerBg: "#234935",
    pillars: ["Pessoas", "Processos", "Resultados"],
    story: `Aqui na ${companyName || "nossa empresa"}, valorizamos pessoas como você, que inspiram e transformam o time, reforçando nossa cultura de excelência e crescimento sustentável.`,
    contact: "Grupo ALCON",
    addressShort: companyName || "Grupo ALCON",
  };
}

const fmtBRL = (n) => (n == null ? "—" : Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
};

export default function PromotionLetterDocument({ letter, onClose, onApplied, onEdit }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(null);
  const [applying, setApplying] = useState(false);
  const theme = getCompanyTheme(letter.company_nome_fantasia);

  const downloadImage = async () => {
    if (!ref.current) return;
    setBusy("img");
    try {
      const canvas = await html2canvas(ref.current, {
        scale: 2, useCORS: true, allowTaint: false, backgroundColor: "#ffffff",
      });
      canvas.toBlob((blob) => {
        if (!blob) throw new Error("blob");
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Carta-Promocao-${letter.employee_name}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Imagem baixada.");
      }, "image/png");
    } catch (e) {
      toast.error("Falha ao gerar imagem.");
    } finally { setBusy(null); }
  };

  const downloadPdf = async () => {
    if (!ref.current) return;
    setBusy("pdf");
    try {
      const canvas = await html2canvas(ref.current, {
        scale: 2, useCORS: true, backgroundColor: "#ffffff",
      });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      // fit width keeping ratio
      const ratio = canvas.height / canvas.width;
      const imgW = pageW - 16;
      const imgH = imgW * ratio;
      let y = 8;
      if (imgH <= pageH - 16) {
        pdf.addImage(img, "PNG", 8, y, imgW, imgH);
      } else {
        // multi-page: slice
        const pageImgH = pageH - 16;
        const sliceH = (canvas.width / imgW) * pageImgH;
        let sY = 0;
        while (sY < canvas.height) {
          const c2 = document.createElement("canvas");
          c2.width = canvas.width;
          c2.height = Math.min(sliceH, canvas.height - sY);
          const ctx = c2.getContext("2d");
          ctx.drawImage(canvas, 0, sY, canvas.width, c2.height, 0, 0, canvas.width, c2.height);
          pdf.addImage(c2.toDataURL("image/png"), "PNG", 8, 8, imgW, (c2.height / canvas.width) * imgW);
          sY += c2.height;
          if (sY < canvas.height) pdf.addPage();
        }
      }
      pdf.save(`Carta-Promocao-${letter.employee_name}.pdf`);
      toast.success("PDF baixado.");
    } catch (e) {
      toast.error("Falha ao gerar PDF.");
    } finally { setBusy(null); }
  };

  const apply = async () => {
    if (!window.confirm(`Aplicar a promoção ao cadastro de ${letter.employee_name}?\n\nIsto vai atualizar Cargo e Salário.`)) return;
    setApplying(true);
    try {
      await api.post(`/promotion-letters/${letter.id}/apply`, { update_employee: true });
      toast.success("Promoção aplicada ao cadastro.");
      onApplied && onApplied();
      onClose && onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setApplying(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent
        className="sm:max-w-[860px] max-h-[94vh] overflow-y-auto p-0 gap-0"
        aria-describedby="lt-d"
        data-testid="pl-preview-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border bg-white sticky top-0 z-10">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-[18px]">
              <span style={{ color: theme.primary }} className="font-display font-semibold">Carta Gerada</span>
            </DialogTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={downloadPdf} disabled={busy !== null}
                      style={{ backgroundColor: theme.primary, color: "white" }}
                      className="rounded-full px-4 hover:opacity-90"
                      data-testid="pl-download-pdf">
                <FileDown className={`h-3.5 w-3.5 mr-1.5 ${busy === "pdf" ? "animate-pulse" : ""}`} />
                Baixar PDF
              </Button>
              <Button size="sm" onClick={downloadImage} disabled={busy !== null}
                      style={{ backgroundColor: theme.key === "alcon" ? "#1A2C58" : "#1A2C58", color: "white" }}
                      className="rounded-full px-4 hover:opacity-90"
                      data-testid="pl-download-img">
                <ImageIcon className={`h-3.5 w-3.5 mr-1.5 ${busy === "img" ? "animate-pulse" : ""}`} />
                Baixar Imagem
              </Button>
              {onEdit && (
                <Button size="sm" variant="outline" onClick={onEdit}
                        className="rounded-full px-4"
                        data-testid="pl-edit-data">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Editar Dados
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>
        <p id="lt-d" className="sr-only">Carta de Promoção</p>

        <div className="bg-slate-100 p-3 sm:p-6">
          <div ref={ref}
               className="mx-auto bg-white shadow-xl"
               style={{ width: "100%", maxWidth: 760, fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" }}
               data-testid="letter-document">
            {/* HERO */}
            <div className="relative overflow-hidden" style={{ minHeight: 320 }}>
              <div className="absolute inset-0 bg-cover bg-center"
                   style={{ backgroundImage: `url(${HERO_IMG})` }} />
              <div className="absolute inset-0" style={{ background: theme.heroGradient }} />
              <div className="absolute left-0 top-6 bottom-6 w-1.5" style={{ background: theme.secondary }} />
              <div className="relative px-8 pt-8 pb-6 z-10">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-white">
                    <div className="text-[26px] font-bold tracking-wide uppercase mb-3">
                      {(letter.employee_name || "").toUpperCase()}
                    </div>
                    <p className="text-white/90 text-[12.5px] leading-relaxed max-w-[520px]">
                      o sucesso de uma empresa é<br/>
                      o resultado do engajamento<br/>
                      de pessoas como
                    </p>
                  </div>
                  <div className="flex gap-1 mt-1.5">
                    <span className="block h-1.5 w-1.5 rounded-full bg-white/80" />
                    <span className="block h-1.5 w-1.5 rounded-full bg-white/80" />
                    <span className="block h-1.5 w-1.5 rounded-full bg-white/80" />
                  </div>
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div className="text-white text-[88px] font-bold leading-none italic"
                       style={{ fontFamily: "Georgia, serif", textShadow: "0 4px 24px rgba(0,0,0,0.25)" }}>
                    você
                  </div>
                  {letter.company_logo_url && (
                    <div className="bg-white rounded-md p-2 shadow-md">
                      <img src={letter.company_logo_url} alt="logo"
                           crossOrigin="anonymous"
                           className="h-12 w-auto object-contain" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* TITLE */}
            <div className="px-8 pt-6 pb-4 flex items-start justify-between gap-3 border-b border-slate-100">
              <div>
                <p className="text-[12.5px] text-slate-700 italic mb-0.5">e você é a nossa escolha para</p>
                <h2 className="text-[28px] font-bold leading-tight" style={{ color: theme.primary, fontFamily: "Georgia, serif" }}>
                  conquistar ainda mais!
                </h2>
              </div>
              {letter.company_logo_url && (
                <img src={letter.company_logo_url} alt="logo"
                     crossOrigin="anonymous"
                     className="h-10 w-auto object-contain mt-1" />
              )}
            </div>

            {/* BODY */}
            <div className="px-8 py-5 space-y-3 text-[12.5px] text-slate-800 leading-relaxed">
              <p>
                É com muita alegria que celebramos sua promoção ao cargo de{" "}
                <b style={{ color: theme.primary }}>{letter.new_cargo}</b>, para a estrutura da{" "}
                <b style={{ color: theme.primary }}>{letter.company_nome_fantasia} — {theme.addressShort.split("—").slice(1).join("—").trim() || "Conceição da Barra/ES"}</b>.
              </p>
              <p>{theme.story}</p>

              {/* SALARY CARD */}
              <div className="rounded-lg border bg-white p-4 shadow-sm" style={{ borderColor: theme.tint }}>
                <div className="text-[12px] mb-2" style={{ color: theme.primary }}>
                  <b>Sua nova composição salarial:</b>
                </div>
                <div className="rounded-md flex items-center justify-between px-4 py-3"
                     style={{ background: theme.accentBand, color: "white" }}>
                  <span className="text-[13px]">Salário Base:</span>
                  <span className="text-[18px] font-bold tabular-nums">{fmtBRL(letter.new_salario)}</span>
                </div>
                {letter.diff_pct != null && letter.salario_anterior && (
                  <div className="mt-2 text-[10.5px] text-slate-500">
                    De {fmtBRL(letter.salario_anterior)} → <b style={{ color: theme.primary }}>+{letter.diff_pct}%</b>
                  </div>
                )}
              </div>

              <p>
                Investimos no desenvolvimento de nossos colaboradores, oferecendo oportunidades para que demonstrem
                sua capacidade e alcancem seu potencial. Valorizamos a sinergia e o comprometimento em todos os setores.
              </p>

              {/* QUOTE */}
              <div className="rounded-lg p-5 mt-2" style={{ background: theme.primary, color: "white" }}>
                <Quote className="h-5 w-5 mb-1 opacity-80" />
                <p className="text-[13px] italic leading-relaxed">
                  "Sua trajetória até aqui demonstra dedicação, competência e compromisso com a excelência.
                  Agora, como <b>{letter.new_cargo}</b>, você terá a oportunidade de multiplicar esse conhecimento
                  e inspirar toda a equipe. Conte conosco nessa nova jornada!"
                </p>
              </div>

              {/* OBSERVATION */}
              <div className="rounded-md bg-amber-50 border-l-4 border-amber-300 px-4 py-3 mt-2">
                <p className="text-[12px] text-slate-700">
                  <b style={{ color: theme.primary }}>Observação:</b> {letter.observacoes ||
                    "A promoção será efetivada após a conclusão dos exames ocupacionais."}
                </p>
              </div>

              <div className="text-[11px] text-slate-500 mt-3">
                Data efetiva: <b>{fmtDate(letter.data_efetiva)}</b> · Emitida em {fmtDate(letter.issued_at)}
              </div>
            </div>

            {/* FOOTER PILLARS */}
            <div className="text-white px-8 py-6" style={{ background: theme.footerBg }}>
              <div className={`grid gap-3 mb-3 ${
                theme.pillars.length === 4 ? "grid-cols-4" :
                theme.pillars.length === 3 ? "grid-cols-3" : "grid-cols-2"
              }`}>
                {theme.pillars.map((p) => (
                  <div key={p} className="text-center">
                    <div className="mx-auto h-7 w-7 rounded-full bg-white/15 grid place-items-center mb-1.5">
                      <span className="block h-3 w-3 rounded-full bg-white" />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-semibold">{p}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/20 pt-3 text-center">
                <div className="font-display text-[13.5px] font-semibold tracking-wide">
                  {letter.company_nome_fantasia} — Grupo ALCON
                </div>
                <div className="text-[10.5px] text-white/80 mt-0.5 whitespace-pre-line">
                  {theme.contact}
                </div>
              </div>
            </div>
          </div>

          {/* Action footer */}
          <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] text-slate-500">
              <b>Nº</b> <span className="font-mono">{letter.id.substring(0, 8).toUpperCase()}</span>
              {letter.applied_to_employee && (
                <span className="ml-3 inline-flex items-center text-emerald-700">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Promoção aplicada ao cadastro
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!letter.applied_to_employee && (
                <Button onClick={apply} disabled={applying}
                        style={{ background: theme.primary }}
                        className="text-white hover:opacity-90"
                        data-testid="pl-apply-btn">
                  <CheckCircle2 className={`h-3.5 w-3.5 mr-1.5 ${applying ? "animate-pulse" : ""}`} />
                  Aplicar promoção ao cadastro
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>Fechar</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
